import { useStaff, useThread, useThreadMetrics, useThreads } from '@zuruflex/api-client/react';
import type { SlaTargets } from '@zuruflex/core';
import {
  Button,
  Checkbox,
  EmptyState,
  ErrorState,
  PageHeader,
  Skeleton,
  SlaIndicator,
  ThreadRow,
} from '@zuruflex/ui';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api } from '../../lib/api.js';
import { BulkBar, BulkResultAlert, type BulkResult } from './BulkBar.js';
import { ContextPane } from './ContextPane.js';
import { ConversationPane } from './ConversationPane.js';
import { FilterRail } from './FilterRail.js';
import { narrowToView, toThreadQuery, useInboxFilters } from './filters.js';

/**
 * The staffed inbox — the thing whose absence the concierge apologises for
 * when it hands a customer to a human.
 *
 * Three panes, which is one more than either of the other two apps needs:
 * an agent is working a queue rather than holding a conversation, so the
 * filters above and the context on the right are the job, and the middle
 * is only where the job gets done.
 *
 * Which conversation is open lives in the path and which queue is being
 * worked lives in the query string, so a desk is a link: an agent hands
 * over a shift by pasting one, and a tile on the dashboard is the same
 * link with a number on it.
 */
export function InboxPage() {
  const { t } = useTranslation(['admin', 'common']);
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const handle = useInboxFilters();
  const { filters, search, selectionKey } = handle;

  const [selection, setSelection] = useState<string[]>([]);
  /*
   * Held here rather than in the bar, because the bar goes as soon as the
   * last ticked thread takes the change — and the confirmation has to
   * outlive the control that produced it.
   */
  const [bulkResult, setBulkResult] = useState<BulkResult>();

  /*
   * A ticked row that survives a filter change is a bulk action about to
   * be applied to threads the agent can no longer see — so the selection
   * is dropped whenever the queue underneath it changes. Not on a page
   * change either: "select all" means this page, and carrying ticks
   * across pages is how "all matching the filter" gets built by accident.
   */
  useEffect(() => {
    setSelection([]);
    setBulkResult(undefined);
  }, [selectionKey, filters.page]);

  /*
   * The metrics response is the only place an ordinary agent can read the
   * operator's two targets: `GET /v1/settings` is MANAGER and up and would
   * 403 for most of the desk. Asked for over today alone, because the
   * numbers on it are current values rather than a window's, and the scan
   * behind a range is the most expensive read the API has.
   *
   * The range is fixed at mount so the query key is stable — a `new Date()`
   * in the key would refetch on every render.
   */
  const today = useMemo(() => {
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    return { from: start.toISOString(), to: new Date().toISOString() };
  }, []);
  const metrics = useThreadMetrics(api, today);
  const targets: SlaTargets | null = metrics.data
    ? {
        firstResponseTargetMinutes: metrics.data.targets.firstResponseMinutes,
        resolutionTargetHours: metrics.data.targets.resolutionHours,
      }
    : null;

  const staff = useStaff(api);
  const threads = useThreads(api, toThreadQuery(filters));

  /*
   * One clock for the whole page, and it is the moment the rows landed
   * rather than the moment they were painted. Forty rows each reading
   * their own `Date.now()` disagree with each other by a render, and a
   * clock pinned to the fetch moves with the twenty-second poll — which is
   * as often as any of the figures behind it can have changed.
   */
  const now = useMemo(() => new Date(threads.dataUpdatedAt || Date.now()), [threads.dataUpdatedAt]);

  const page = threads.data;
  const items = narrowToView(page?.items ?? [], filters.view, targets, now);

  /* The bare `/inbox` lands on the first row, as the partner portal does. */
  const first = items[0]?.id;
  useEffect(() => {
    if (!id && first) navigate(`/inbox/${first}${search}`, { replace: true });
  }, [id, first, search, navigate]);

  const thread = useThread(api, id);

  const allTicked = items.length > 0 && items.every((row) => selection.includes(row.id));

  return (
    <div className="flex flex-col gap-4 xl:h-[calc(100dvh-4rem)] xl:min-h-[36rem]">
      <PageHeader title={t('inbox.title')} description={t('inbox.subtitle')} />

      <FilterRail handle={handle} staff={staff.data?.items ?? []} />

      {selection.length > 0 ? (
        <BulkBar
          ids={selection}
          staff={staff.data?.items ?? []}
          onResult={setBulkResult}
          onApplied={(succeeded) =>
            setSelection((current) => current.filter((row) => !succeeded.includes(row)))
          }
          onClear={() => {
            setSelection([]);
            setBulkResult(undefined);
          }}
        />
      ) : null}

      {bulkResult ? <BulkResultAlert result={bulkResult} /> : null}

      <div className="grid min-h-0 flex-1 gap-4 xl:grid-cols-[23rem_minmax(0,1fr)_20rem]">
        <div className="flex min-h-0 flex-col gap-2">
          {threads.error ? (
            <ErrorState error={threads.error} onRetry={() => void threads.refetch()} />
          ) : threads.isPending ? (
            <Skeleton className="h-96 w-full" />
          ) : items.length === 0 ? (
            <EmptyState title={t('inbox.empty')} description={t('inbox.emptyBody')} />
          ) : (
            <>
              <div className="flex items-center gap-2 px-2">
                <Checkbox
                  checked={allTicked}
                  // "Select all on this page" and never "all matching the
                  // filter": the second is a promise about rows nobody has
                  // looked at, applied by a request that names ids.
                  aria-label={t('inbox.filter.all')}
                  onCheckedChange={(checked) =>
                    setSelection(checked === true ? items.map((row) => row.id) : [])
                  }
                />
                <span className="text-xs text-text-muted">
                  {t('common:table.pageOf', {
                    page: page?.page ?? 1,
                    total: page?.totalPages ?? 1,
                  })}
                </span>
              </div>

              <ul className="flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto rounded-card border border-border-default bg-surface p-2">
                {items.map((row) => (
                  <li key={row.id} className="flex items-start gap-2">
                    <Checkbox
                      className="mt-4 shrink-0"
                      checked={selection.includes(row.id)}
                      aria-label={row.reference}
                      onCheckedChange={(checked) =>
                        setSelection((current) =>
                          checked === true
                            ? [...current, row.id]
                            : current.filter((other) => other !== row.id),
                        )
                      }
                    />
                    <div className="min-w-0 flex-1">
                      <ThreadRow
                        thread={row}
                        active={row.id === id}
                        as={(children, className) => (
                          // The filters travel with the link, so opening
                          // a thread keeps the queue it was found in.
                          <Link to={`/inbox/${row.id}${search}`} className={className}>
                            {children}
                          </Link>
                        )}
                      />
                      {/*
                          One clock, because the row has space for one and
                          `slaState` already says which is running. The pause
                          is the indicator's own call now — it asks the same
                          rule and draws nothing on a snoozed or resolved
                          row, rather than this deciding whether to show a
                          badge and the badge deciding what colour it is.
                        */}
                      {targets ? (
                        <SlaIndicator
                          className="px-3 pb-2"
                          thread={row}
                          now={now}
                          only={row.firstResponseAt ? 'resolution' : 'firstResponse'}
                          firstResponseTargetMinutes={targets.firstResponseTargetMinutes}
                          resolutionTargetHours={targets.resolutionTargetHours}
                          labels={{
                            firstResponse: t('inbox.sla.firstResponse'),
                            resolution: t('inbox.sla.resolution'),
                            ok: t('inbox.sla.met'),
                            due: t('inbox.sla.due', { time: '' }).trim(),
                            breached: t('inbox.sla.breached'),
                          }}
                        />
                      ) : null}
                    </div>
                  </li>
                ))}
              </ul>

              {(page?.totalPages ?? 1) > 1 ? (
                <div className="flex items-center justify-between gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={filters.page <= 1}
                    onClick={() => handle.set({ page: filters.page - 1 })}
                  >
                    {t('common:action.previous')}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={filters.page >= (page?.totalPages ?? 1)}
                    onClick={() => handle.set({ page: filters.page + 1 })}
                  >
                    {t('common:action.next')}
                  </Button>
                </div>
              ) : null}
            </>
          )}
        </div>

        {!id ? (
          <EmptyState title={t('inbox.pick')} />
        ) : thread.error ? (
          <ErrorState error={thread.error} onRetry={() => void thread.refetch()} />
        ) : thread.isPending ? (
          <Skeleton className="h-full min-h-96 w-full" />
        ) : (
          <ConversationPane thread={thread.data} />
        )}

        {/*
          One `useThread` for the page, and both panes read it. Two calls
          would open two event streams for one conversation — the hook
          listens per mount — and the second would be a socket nobody needs.
        */}
        {id && thread.data ? <ContextPane thread={thread.data} targets={targets} /> : null}
      </div>
    </div>
  );
}
